import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import FilterBar, {
  type FilterGroup,
} from "@/components/feature/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  getEventsBySport,
  regionOptions,
  sportCategories,
  type SportKey,
} from "@/mocks/events";

const PAGE_SIZE = 6;

const bannerImages: Record<SportKey, string> = {
  basketball:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20wide%20view%20of%20an%20empty%20indoor%20basketball%20arena%20with%20warm%20orange%20spotlights%20and%20deep%20black%20shadows%2C%20atmospheric%20haze%2C%20professional%20sports%20broadcast%20banner%2C%20high%20contrast%2C%20no%20text&width=1920&height=640&seq=jrs-banner-basketball&orientation=landscape",
  football:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20wide%20view%20of%20a%20floodlit%20football%20stadium%20pitch%20at%20night%20with%20warm%20orange%20rim%20light%20and%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20sports%20broadcast%20banner%2C%20high%20contrast%2C%20no%20text&width=1920&height=640&seq=jrs-banner-football&orientation=landscape",
  esports:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20wide%20view%20of%20an%20esports%20tournament%20stage%20with%20orange%20and%20red%20neon%20beams%20and%20giant%20glowing%20screens%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20gaming%20banner%2C%20high%20contrast%2C%20no%20text&width=1920&height=640&seq=jrs-banner-esports&orientation=landscape",
};

const introCopy: Record<SportKey, string> = {
  basketball:
    "篮球专区汇聚 NBA、CBA 等顶级联赛的实时战况，从常规赛到季后赛，比分、关键球与球员数据同步更新，陪你追随每一支心爱的球队。",
  football:
    "足球专区覆盖英超、西甲、德甲、意甲、法甲等五大联赛及洲际杯赛，进球、助攻与战术变化即时呈现，不错过任何一粒精彩进球。",
  esports:
    "电竞专区聚合英雄联盟、王者荣耀、CS2 等热门赛事，团战高光、选手镜头与复盘数据一应俱全，见证冠军的每一次诞生。",
};

export default function CategoryPage() {
  const { sport } = useParams<{ sport: string }>();
  const isValid = sport === "basketball" || sport === "football" || sport === "esports";
  const sportKey = (isValid ? sport : "basketball") as SportKey;
  const category = sportCategories.find((c) => c.key === sportKey)!;

  const [status, setStatus] = useState("all");
  const [region, setRegion] = useState("all");
  const [time, setTime] = useState("all");
  const [page, setPage] = useState(1);

  const title = `${category.name}赛事直播 - jrs直播`;
  const description = `${catDescription(category.name)} jrs直播${category.name}专区提供正在进行、即将开始与已结束赛事的实时比分、赛程与直播入口。`;

  usePageMeta(
    title,
    description,
    `${category.name}直播,jrs直播,${category.name}赛事,${category.tagline.replace(/[·\s]/g, ",")},体育直播`,
  );

  const filterGroups: FilterGroup[] = [
    {
      key: "status",
      label: "赛事状态",
      options: [
        { label: "全部状态", value: "all" },
        { label: "直播中", value: "live" },
        { label: "已结束", value: "ended" },
        { label: "预告", value: "upcoming" },
      ],
    },
    {
      key: "region",
      label: "赛区",
      options: regionOptions[sportKey].map((r) => ({
        label: r,
        value: r === "全部赛区" ? "all" : r,
      })),
    },
    {
      key: "time",
      label: "时间",
      options: [
        { label: "全部时间", value: "all" },
        { label: "最近赛事", value: "recent" },
        { label: "未来赛事", value: "future" },
      ],
    },
  ];

  const filtered = useMemo(() => {
    if (!isValid) return [];
    return getEventsBySport(sportKey).filter((event) => {
      if (status !== "all" && event.status !== status) return false;
      if (region !== "all" && event.region !== region) return false;
      if (time === "recent" && event.status !== "ended") return false;
      if (time === "future" && event.status !== "upcoming") return false;
      return true;
    });
  }, [isValid, sportKey, status, region, time]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleChange = (key: string, value: string) => {
    if (key === "status") setStatus(value);
    if (key === "region") setRegion(value);
    if (key === "time") setTime(value);
    setPage(1);
  };

  const handleReset = () => {
    setStatus("all");
    setRegion("all");
    setTime("all");
    setPage(1);
  };

  if (!isValid) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-[720px] flex-col items-center justify-center px-4 py-20 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          未找到该赛事分类
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的分类不存在，可能链接有误。返回首页或选择以下热门分类继续浏览。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
          {sportCategories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="rounded-md border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
            >
              {c.name}直播
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="relative w-full overflow-hidden border-b border-background-200">
        <img
          src={bannerImages[sportKey]}
          alt={`${category.name}赛事直播横幅`}
          title={`${category.name}直播 - jrs直播`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-transparent" />
        <div className="relative mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{category.name}直播</span>
          </nav>
          <div className="mt-5 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
                {category.name}赛事直播
              </h1>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-primary-400">
                {category.enName} · {category.tagline}
              </p>
            </div>
          </div>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {introCopy[sportKey]}
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-12">
        <FilterBar
          groups={filterGroups}
          values={{ status, region, time }}
          onChange={handleChange}
          onReset={handleReset}
        />

        <div className="mt-6 flex items-center justify-between gap-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 场
            {category.name}赛事
          </p>
          <span className="text-xs text-foreground-500">
            第 {safePage} / {totalPages} 页
          </span>
        </div>

        {paged.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {paged.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-line text-3xl" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              没有符合条件的赛事
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              当前筛选条件下暂时没有{category.name}赛事，试试放宽筛选条件，或重置后查看全部赛程。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-primary-400 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-9 min-w-9 rounded-md border px-3 text-sm font-semibold transition-colors cursor-pointer ${
                  p === safePage
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-500/50 hover:text-primary-400"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-primary-400 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function catDescription(name: string): string {
  const map: Record<string, string> = {
    篮球: "汇集 NBA、CBA 等顶级篮球联赛的精彩对决，实时比分与直播入口一应俱全。",
    足球: "覆盖英超、西甲等五大联赛及洲际杯赛的足球盛宴，进球与赛况同步更新。",
    电竞: "聚合 LPL、KPL、CS2 等热门电竞赛事，团战高光与冠军争夺一站看齐。",
  };
  return map[name] || "为你提供丰富的赛事直播内容。";
}